import React, { useId, useState } from "react";
import { Button, Callout, Dialog, FileBadge, FileDropZone } from "@sscs/ui";
import { UploadIcon } from "@sscs/ui/icons";
import { humanReadableBytes } from "@sscs/utils";
import { AllowedTo } from "Components";
import { PERMISSION, PROJECTS_ENDPOINT } from "Constants";
import { FILE_NAME_CHARACTERS_LIMIT } from "Modules/upload";
import { uploadFile, useUploadConfigQuery } from "Services";
import { apiErrorToast } from "Utils";

type UploadReproBuildDialogProps = {
  versionId: string;
  file: File | null;
  onClose: () => void;
};

const UploadReproBuildContent = ({
  versionId,
  file,
  onClose,
}: Omit<UploadReproBuildDialogProps, "file"> & {
  file: File;
}): React.JSX.Element => {
  const {
    data: config = {
      size_limit: 1024 * 1024 * 1024,
      max_versions_per_package: 0,
    },
  } = useUploadConfigQuery();
  const isOverSizeLimit = file.size > config.size_limit;
  const isFileNameTooLong = file.name.length > FILE_NAME_CHARACTERS_LIMIT;

  const handleUpload = async (): Promise<void> => {
    if (isOverSizeLimit || isFileNameTooLong) return;

    const formData = new FormData();
    formData.append("file", file, file.name);

    try {
      const uploadReproPromise = uploadFile(
        `${PROJECTS_ENDPOINT}/versions/${versionId}/repro`,
        formData,
        1,
      );

      onClose();

      await uploadReproPromise;
    } catch (error) {
      apiErrorToast(error, "Could not upload the reproducible build");
      onClose();
    }
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>Upload reproducible build</Dialog.Header.Title>
      </Dialog.Header>
      <Dialog.Body>
        <Dialog.Section>
          <Dialog.Section.Content>
            <FileBadge path={file.name} size={file.size} sha256={null} />
            {isOverSizeLimit && (
              <Callout type="error" title="Size limit exceeded">
                Selected file (
                <strong>{humanReadableBytes(file.size, 2)}</strong>) exceeds
                upload limit (
                <strong>{humanReadableBytes(config.size_limit, 2)}</strong>)
              </Callout>
            )}
            {isFileNameTooLong && (
              <Callout type="error" title="File name length limit exceeded">
                Selected file (<strong>{file.name.length}</strong>) exceeds
                upload limit (<strong>{FILE_NAME_CHARACTERS_LIMIT}</strong>)
              </Callout>
            )}
          </Dialog.Section.Content>
        </Dialog.Section>
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Footer.Actions>
          <Button id="cancel" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button id="upload" color="primary" onClick={handleUpload}>
            Upload
          </Button>
        </Dialog.Footer.Actions>
      </Dialog.Footer>
    </>
  );
};

const UploadReproBuildDialog = ({
  versionId,
  file,
  onClose,
}: UploadReproBuildDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={file !== null} onClose={onClose}>
    {file && (
      <UploadReproBuildContent
        versionId={versionId}
        file={file}
        onClose={onClose}
      />
    )}
  </Dialog.Frame>
);

type UploadReproBuildProps = {
  versionId: string;
  checkCount: number;
};

const UploadReproBuild = ({
  versionId,
  checkCount,
}: UploadReproBuildProps): React.JSX.Element => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputId = useId();

  return (
    <>
      <AllowedTo permissions={[PERMISSION.UPLOAD_VERSION]}>
        <FileDropZone
          size="small"
          onDrop={([dropped]) => {
            if (dropped) setSelectedFile(dropped);
          }}
          icon={<UploadIcon size="inherit" />}
          title="Upload a reproducible build"
          description={`Runs ${checkCount} more checks. Drag and drop a file here, or choose one from this device.`}
          dropTitle="Drop to upload"
        >
          <label htmlFor={inputId}>
            <input
              hidden
              id={inputId}
              type="file"
              multiple={false}
              onChange={(event) => {
                const chosen = event.target.files?.[0];
                if (chosen) setSelectedFile(chosen);
              }}
            />
            <Button
              id="upload-reproducible-build"
              color="secondary"
              component="span"
            >
              Choose file
            </Button>
          </label>
        </FileDropZone>
      </AllowedTo>
      <UploadReproBuildDialog
        versionId={versionId}
        file={selectedFile}
        onClose={() => setSelectedFile(null)}
      />
    </>
  );
};

export default UploadReproBuild;
